import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalPolicyPush } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

import { personalSourceOfFeature } from "./personal-policy-utils"
import { PersonalSourceFeatureForm } from "./personal-source-feature-form"
import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { KeyValueOverrides } from "./shared/key-value-overrides"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditFeatureDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  featureId: string | null
  /**
   * Personal-source features only: what happened to the push to FILTH after a save.
   * The caller owns the confirmation dialog, so it survives this dialog closing.
   */
  onPolicyPush?: (push: PersonalPolicyPush | undefined) => void
}

type StoredFeature = {
  name?: string
  description?: string
  enabled?: boolean
  access?: Record<string, unknown>
  config?: Record<string, unknown>
  [key: string]: unknown
}

/**
 * Edit dialog for a feature entity.
 *
 * - **Form** tab surfaces the commonly-edited fields (enabled, display name,
 *   description, and the feature's free-form config bag).
 * - **Raw JSON** tab shows the full stored feature document so admins can
 *   add forward-compatible keys without waiting for a new form field.
 *
 * Both tabs write through ``PUT /admin/features/<id>/raw`` with an audit
 * entry. Form saves set ``source="form"``; Raw saves set ``source="raw"``.
 *
 * The three ``personal_source_*`` features are the exception: the server refuses them
 * on the raw route, so they get their own form (enablement and history window in one
 * validated call, with the explicit widening confirmation) and no Raw tab.
 */
export function EditFeatureDialog({
  isOpen,
  onOpenChange,
  featureId,
  onPolicyPush,
}: EditFeatureDialogProps) {
  const { t } = useTranslation()
  const endpoint = featureId ? `/admin/features/${encodeURIComponent(featureId)}/raw` : null

  const {
    data: rawData,
    isLoading,
    refetch,
  } = useRawEntityQuery<StoredFeature>(endpoint, isOpen && !!endpoint)

  const saveMutation = useSaveRawEntityMutation<StoredFeature>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [["admin", "features"]],
  })

  const stored: StoredFeature = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""

  const [enabled, setEnabled] = useState(true)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [config, setConfig] = useState<Record<string, unknown>>({})
  const [resetSignal, setResetSignal] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    setEnabled(stored.enabled ?? true)
    setName(typeof stored.name === "string" ? stored.name : "")
    setDescription(typeof stored.description === "string" ? stored.description : "")
    setConfig(stored.config && typeof stored.config === "object" ? { ...stored.config } : {})
    setResetSignal((n) => n + 1)
  }, [stored])

  useEffect(() => {
    if (!isOpen) setFormError(null)
  }, [isOpen])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!featureId || !endpoint || !hash) return
    setFormError(null)

    const next: StoredFeature = {
      ...stored,
      enabled,
      name: name.trim(),
      description: description.trim(),
      config,
    }

    try {
      await saveMutation.mutateAsync({
        entity: next as Record<string, unknown>,
        expectedHash: hash,
        reason: t("adminDashboard.featureEdit.reasonForm", {
          defaultValue: "Form-tab edit of feature {{id}}",
          id: featureId,
        }),
        source: "form",
      })
      void refetch()
      toast.success(t("adminDashboard.featureEdit.toastSaved", { defaultValue: "Feature updated" }))
      onOpenChange(false)
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.featureEdit.errorSaveFailed", { defaultValue: "Save failed" })
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!featureId || !endpoint || !hash) return
    try {
      await saveMutation.mutateAsync({
        entity: (next || {}) as Record<string, unknown>,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.featureEdit.toastSavedRaw", { defaultValue: "Feature saved (raw)" })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.featureEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!featureId) return null

  const personalSource = personalSourceOfFeature(featureId)
  if (personalSource) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.featureEdit.title", { defaultValue: "Edit feature" })}{" "}
              <code className="font-mono text-sm">{featureId}</code>
            </DialogTitle>
            <DialogDescription>
              {t("adminDashboard.personalSources.form.intro", {
                defaultValue:
                  "Turn this personal source on or off and set how far back it indexes.",
              })}
            </DialogDescription>
          </DialogHeader>
          <PersonalSourceFeatureForm
            // Re-seed the form whenever the stored feature is read again.
            key={`${featureId}:${hash}`}
            isLoading={isLoading}
            source={personalSource}
            stored={stored}
            onCancel={() => onOpenChange(false)}
            onSaved={(push) => {
              void refetch()
              onOpenChange(false)
              onPolicyPush?.(push)
            }}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.featureEdit.title", { defaultValue: "Edit feature" })}{" "}
            <code className="font-mono text-sm">{featureId}</code>
            {stored.name ? (
              <span className="text-muted-foreground text-sm font-normal"> ({stored.name})</span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.featureEdit.description", {
              defaultValue:
                "Edit display name, description, and config. The Raw tab shows the full stored feature document.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.featureEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.featureEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4 max-h-[60vh] overflow-y-auto" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="feature-enabled">
                    {t("adminDashboard.featureEdit.fieldEnabled", { defaultValue: "Enabled" })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.featureEdit.fieldEnabledHelp", {
                      defaultValue: "Disabled features are hidden in the product UI.",
                    })}
                  </p>
                </div>
                <Switch checked={enabled} id="feature-enabled" onCheckedChange={setEnabled} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="feature-name">
                  {t("adminDashboard.featureEdit.fieldName", { defaultValue: "Display name" })}
                </Label>
                <Input
                  id="feature-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="feature-description">
                  {t("adminDashboard.featureEdit.fieldDescription", {
                    defaultValue: "Description",
                  })}
                </Label>
                <Textarea
                  id="feature-description"
                  rows={3}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>

              <KeyValueOverrides
                disabled={saveMutation.isPending}
                value={config}
                description={t("adminDashboard.featureEdit.configDescription", {
                  defaultValue: "Feature-specific settings.",
                })}
                label={t("adminDashboard.featureEdit.configLabel", {
                  defaultValue: "Feature config",
                })}
                onChange={setConfig}
              />

              {formError ? <p className="text-destructive text-xs">{formError}</p> : null}

              <DialogFooter>
                <Button
                  disabled={saveMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.featureEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={saveMutation.isPending || isLoading || !hash} type="submit">
                  {saveMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.featureEdit.saving", { defaultValue: "Saving…" })}
                    </>
                  ) : (
                    t("adminDashboard.featureEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={featureId}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.featureEdit.rawResourceLabel", {
                defaultValue: "feature {{id}}",
                id: featureId,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
